import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { compact, keyBy } from 'lodash'
import { Prompt, useHistory, useParams } from 'react-router-dom'
import ModulePanel from '../../ui/ModulePanel/ModulePanel'
import Field from '../../ui/Field/Field'
import Button from '../../ui/Button/Button'
import Spinner from '../../ui/Spinner/Spinner'
import StatusMessage, { useStatusMessage } from '../../ui/StatusMessage/StatusMessage'
import PhotoManager from '../PhotoManager/PhotoManager'
import { getBlogMaterial, listBlogAuthors, listBlogTags, setBlogPublication } from '../../api/blog/blogApi'
import {
  createLocalId,
  getLocalMaterial,
  listLocalAuthors,
  listLocalTags,
  upsertLocalMaterial,
  upsertLocalTag,
} from '../../api/blog/blogLocalStore'
import { BLOG_STATUS, BLOG_SYSTEM_AUTHOR_ID, getBlogStatusLabel } from '../../api/blog/blogConstants'
import {
  INDEXATION_MODE,
  INDEXATION_MODE_OPTIONS,
  REVIEW_STATUS,
  REVIEW_STATUS_OPTIONS,
  SEO_SOFT_LIMITS,
} from '../Products/seo/defaultSeo'
import CharCounterHint from '../Products/seo/CharCounterHint'
import RelatedBrandsMultiSelect from '../Marketing/UtmBuilder/RelatedBrandsMultiSelect'
import '../Marketing/UtmBuilder/UtmBuilder.sass'
import BlogBlockCanvas from './editor/BlogBlockCanvas'
import BlogArticlePreviewPane from './editor/BlogArticlePreviewPane'
import useBlogAutosave from './editor/useBlogAutosave'
import BlogVersionConflict from './editor/BlogVersionConflict'
import { createEmptyBlock } from './editor/blockFactory'
import { slugifyBlogTitle } from '../../api/blog/blogSlug'
import editorStyles from './editor/BlogEditor.module.scss'
import styles from './Blog.module.scss'
import DeleteBlogMaterialPopUp from './DeleteBlogMaterialPopUp'

const blankMeta = () => ({
  title: '',
  slug: '',
  excerpt: '',
  authorId: BLOG_SYSTEM_AUTHOR_ID,
  categoryId: null,
  tagIds: [],
  seoTitle: '',
  seoDescription: '',
  seoOgTitle: '',
  seoOgDescription: '',
  seoOgPhotos: [],
  seoReviewStatus: REVIEW_STATUS.NOT_REVIEWED,
  seoIndexationMode: INDEXATION_MODE.AUTO,
  coverPhotos: [],
})

const photoRefFromPhotos = (photos) => {
  if (!Array.isArray(photos) || !photos.length) return null
  const photo = photos[0]
  const imageUrl = photo?.imageUrl || photo?.publicUrl || null
  if (!imageUrl) return null
  return {
    imageUrl,
    storagePath: photo.storagePath || null,
    fileName: photo.fileName || null,
  }
}

const photosFromPhotoRef = (ref, fallbackName = 'image') => {
  if (!ref?.imageUrl) return []
  return [
    {
      imageUrl: ref.imageUrl,
      storagePath: ref.storagePath || null,
      fileName: ref.fileName || fallbackName,
      needUpload: false,
      isAvatar: true,
    },
  ]
}

const metaFromDraft = (material) => {
  const draft = material?.draft || {}
  const seo = draft.seo || {}
  return {
    title: draft.title || material?.title || '',
    slug: draft.slug || material?.slug || '',
    excerpt: draft.excerpt || '',
    authorId: draft.authorId || BLOG_SYSTEM_AUTHOR_ID,
    categoryId: draft.categoryId || null,
    tagIds: Array.isArray(draft.tagIds) ? draft.tagIds : [],
    seoTitle: seo.title || '',
    seoDescription: seo.description || '',
    seoOgTitle: seo.ogTitle || '',
    seoOgDescription: seo.ogDescription || '',
    seoOgPhotos: photosFromPhotoRef(seo.ogImage, 'og'),
    seoReviewStatus: seo.reviewStatus || REVIEW_STATUS.NOT_REVIEWED,
    seoIndexationMode: seo.indexationMode || INDEXATION_MODE.AUTO,
    coverPhotos: photosFromPhotoRef(draft.cover, 'cover'),
  }
}

const buildMaterialRecord = ({ id, meta, blocks, status = BLOG_STATUS.draft, existing = null }) => {
  const now = Date.now()
  return {
    ...(existing || {}),
    id,
    status,
    slug: meta.slug || '',
    title: meta.title || '',
    authorId: meta.authorId,
    categoryId: meta.categoryId,
    tagIds: meta.tagIds,
    draft: {
      ...(existing?.draft || {}),
      schemaVersion: 1,
      title: meta.title || '',
      slug: meta.slug || '',
      excerpt: meta.excerpt || '',
      authorId: meta.authorId,
      categoryId: meta.categoryId,
      tagIds: meta.tagIds,
      cover: photoRefFromPhotos(meta.coverPhotos),
      seo: {
        title: meta.seoTitle || '',
        description: meta.seoDescription || '',
        ogTitle: meta.seoOgTitle || '',
        ogDescription: meta.seoOgDescription || '',
        ogImage: photoRefFromPhotos(meta.seoOgPhotos),
        reviewStatus: meta.seoReviewStatus || REVIEW_STATUS.NOT_REVIEWED,
        indexationMode: meta.seoIndexationMode || INDEXATION_MODE.AUTO,
      },
      blocks,
      toc: existing?.draft?.toc || [],
      readingTimeMinutes: existing?.draft?.readingTimeMinutes || 1,
      contentSavedAt: now,
    },
    updatedAtSort: now,
  }
}

function BlogMaterialEditor() {
  const { id } = useParams()
  const history = useHistory()
  const isNewRoute = !id || id === 'new'
  const [statusMessage, setStatusMessage] = useStatusMessage()
  const [loadStatus, setLoadStatus] = useState(isNewRoute ? 'ready' : 'loading')
  const [meta, setMeta] = useState(blankMeta)
  const [authors, setAuthors] = useState([])
  const [tags, setTags] = useState([])
  const [isPublishing, setIsPublishing] = useState(false)
  const [showDelete, setShowDelete] = useState(false)
  const [materialStatus, setMaterialStatus] = useState(BLOG_STATUS.draft)
  const [materialId, setMaterialId] = useState(() => (isNewRoute ? createLocalId() : id))
  const [blocks, setBlocks] = useState(() => [createEmptyBlock('paragraph')])
  const [settingsOpen, setSettingsOpen] = useState(isNewRoute)
  const [selectedBlockId, setSelectedBlockId] = useState(null)
  const [activeEditor, setActiveEditor] = useState(null)
  const [existingSnapshot, setExistingSnapshot] = useState(null)
  const seededRef = useRef(false)
  const slugTouchedRef = useRef(false)
  const activeRouteRef = useRef(id)

  useEffect(() => {
    activeRouteRef.current = id
    setShowDelete(false)
    return () => { activeRouteRef.current = null }
  }, [id])

  const materialRecord = useMemo(
    () =>
      buildMaterialRecord({
        id: materialId,
        meta,
        blocks,
        status: materialStatus,
        existing: existingSnapshot,
      }),
    [materialId, meta, blocks, materialStatus, existingSnapshot]
  )

  const latestRecordRef = useRef(materialRecord)
  latestRecordRef.current = materialRecord

  const handleSaved = useCallback((record, unchanged) => {
    setExistingSnapshot(record)
    setMaterialStatus(record.status || BLOG_STATUS.draft)
    if (unchanged) {
      setMeta(metaFromDraft(record))
      setBlocks(record.draft.blocks)
      setTags(listLocalTags())
    }
  }, [])

  const { status: autosaveStatus, isSaving, saveNow, markSynced, discard, conflict, inspectServerVersion, resolveConflict, downloadBackup, hasUnpersistedChanges } = useBlogAutosave({
    materialRecord,
    enabled: Boolean(materialId) && loadStatus === 'ready',
    onSaved: handleSaved,
  })


  const handleSave = async () => {
    if (isSaving || isPublishing) return
    const routeId = id
    setStatusMessage({ show: false, type: '', message: '', closeAfter: null })
    const result = await saveNow()
    if (activeRouteRef.current !== routeId) return

    const saved = result.ok && result.unchanged !== false
    setStatusMessage({
      show: true,
      type: saved ? 'success' : 'fail',
      message: saved
        ? `Дані збережено${result.softWarn ? '. Стаття наближається до ліміту розміру.' : ''}`
        : result.ok
          ? 'Під час збереження внесено нові зміни. Натисніть «Зберегти» ще раз.'
          : result.message || 'Не вдалося зберегти. Спробуйте ще раз.',
      closeAfter: saved ? 5000 : 7000,
    })
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }

  const handlePublication = async (publish) => {
    if (isSaving || isPublishing) return
    const routeId = id
    setIsPublishing(true)
    setStatusMessage({ show: false, type: '', message: '', closeAfter: null })
    try {
      const saved = await saveNow()
      if (!saved.ok || saved.unchanged === false) throw new Error(saved.message || 'Збережіть поточні зміни перед публікацією')
      const result = await setBlogPublication({ materialId, expectedRevision: saved.material.draftRevision, publish })
      if (!result.ok || !result.material) throw new Error(result.message || 'Не вдалося змінити публікацію')
      if (activeRouteRef.current !== routeId) return
      const current = latestRecordRef.current
      const savedFingerprint = JSON.stringify({ ...saved.material.draft, contentSavedAt: 0 })
      const currentFingerprint = JSON.stringify({ ...current.draft, contentSavedAt: 0 })
      const unchanged = currentFingerprint === savedFingerprint
      const record = { ...result.material, draft: unchanged ? result.material.draft : current.draft, _serverSynced: unchanged }
      setExistingSnapshot(record)
      setMaterialStatus(record.status)
      const persisted = await upsertLocalMaterial(record)
      if (activeRouteRef.current !== routeId) return
      markSynced(persisted)
      setTags(listLocalTags())
      setStatusMessage({ show: true, type: 'success', message: publish ? 'Матеріал опубліковано' : 'Матеріал знято з публікації', closeAfter: 5000 })
    } catch (error) {
      if (activeRouteRef.current === routeId) setStatusMessage({ show: true, type: 'fail', message: error.message, closeAfter: 7000 })
    } finally {
      if (activeRouteRef.current === routeId) setIsPublishing(false)
    }
  }

  const patch = useCallback((partial) => {
    setMeta((prev) => ({ ...prev, ...partial }))
  }, [])

  const handleTitleChange = useCallback((event) => {
    const title = event.target.value
    setMeta((prev) => ({
      ...prev,
      title,
      slug: slugTouchedRef.current ? prev.slug : slugifyBlogTitle(title),
    }))
  }, [])

  const handleSlugChange = useCallback((event) => {
    slugTouchedRef.current = true
    setMeta((prev) => ({ ...prev, slug: event.target.value }))
  }, [])

  const categoryOptions = useMemo(() => [
    { value: '', label: 'Оберіть категорію' },
    ...tags.filter((tag) => tag.kind === 'category')
      .sort((a, b) => (a.order || 0) - (b.order || 0) || a.name.localeCompare(b.name, 'uk'))
      .map((tag) => ({ value: tag.id, label: tag.name })),
  ], [tags])
  const topicTags = useMemo(() => tags.filter((tag) => tag.kind !== 'category'), [tags])

  useEffect(() => {
    if (!tags.length || loadStatus !== 'ready' || meta.categoryId) return
    const oldCategories = tags.filter((tag) => tag.kind === 'category' && meta.tagIds.includes(tag.id))
    if (oldCategories.length === 1) {
      const categoryId = oldCategories[0].id
      patch({ categoryId, tagIds: meta.tagIds.filter((id) => id !== categoryId) })
    }
  }, [tags, loadStatus, meta.categoryId, meta.tagIds, patch])

  const selectedTags = useMemo(
    () => {
      const tagsById = keyBy(topicTags, 'id')
      return compact(meta.tagIds.map((id) => tagsById[id]))
    },
    [topicTags, meta.tagIds]
  )

  const authorOptions = useMemo(
    () => authors.map((author) => ({ value: author.id, label: author.displayName || author.id })),
    [authors]
  )

  useEffect(() => {
    let cancelled = false
    Promise.all([listBlogAuthors(), listBlogTags()]).then(([authorsResult, tagsResult]) => {
      if (cancelled) return
      const localAuthors = listLocalAuthors()
      setAuthors(authorsResult.ok && authorsResult.authors.length ? authorsResult.authors : localAuthors)
      if (tagsResult.ok) {
        tagsResult.tags.forEach((tag) => upsertLocalTag(tag))
      }
      setTags(listLocalTags())
    })
    return () => {
      cancelled = true
    }
  }, [])

  useEffect(() => {
    if (!isNewRoute || seededRef.current) return
    seededRef.current = true
    const seeded = latestRecordRef.current
    let cancelled = false
    upsertLocalMaterial(seeded).then((local) => {
      if (cancelled) return
      markSynced(local)
      if (local._localPersist?.ok) history.replace(`/blog/materials/${materialId}`)
    })
    return () => { cancelled = true }
  }, [isNewRoute, materialId, history, markSynced])

  useEffect(() => {
    if (isNewRoute) return undefined
    let cancelled = false
    setLoadStatus('loading')

    const hydrate = (material) => {
      const draft = material.draft || {}
      const nextMeta = metaFromDraft(material)
      slugTouchedRef.current = Boolean(nextMeta.slug)
      setExistingSnapshot(material)
      setMaterialId(material.id)
      setMaterialStatus(material.status || BLOG_STATUS.draft)
      setMeta(nextMeta)
      const loadedBlocks =
        Array.isArray(draft.blocks) && draft.blocks.length
          ? draft.blocks
          : [createEmptyBlock('paragraph')]
      setBlocks(loadedBlocks)
      markSynced(
        buildMaterialRecord({
          id: material.id,
          meta: nextMeta,
          blocks: loadedBlocks,
          status: material.status || BLOG_STATUS.draft,
          existing: material,
        })
      )
      setLoadStatus('ready')
    }

    const load = async () => {
      const cached = await getLocalMaterial(id)
      if (cancelled) return
      const local = cached?.draft ? cached : null
      if (local && !local._serverSynced) hydrate(local)
      const result = await getBlogMaterial(id)
      if (cancelled) return
      if (result.ok && result.material) {
        if (local && !local._serverSynced) {
          inspectServerVersion(result.material)
        } else {
          const remote = { ...result.material, _serverSynced: true }
          void upsertLocalMaterial(remote)
          hydrate(remote)
        }
        return
      }
      if (local?._serverSynced) hydrate(local)
      if (!local) setLoadStatus('missing')
    }
    void load()

    return () => {
      cancelled = true
    }
  }, [id, isNewRoute, markSynced, inspectServerVersion])

  const handleTagsChange = (nextTags) => {
    patch({ tagIds: nextTags.map((tag) => tag.id) })
  }

  if (loadStatus === 'loading') {
    return (
      <div className={styles.editorShell}>
        <ModulePanel title="Матеріал" className={styles.panel}>
          <div className={styles.empty}>
            <Spinner type="module" />
          </div>
        </ModulePanel>
      </div>
    )
  }

  if (loadStatus === 'missing') {
    return (
      <div className={styles.editorShell}>
        <ModulePanel title="Матеріал" className={styles.panel}>
          <p className={styles.empty}>Матеріал не знайдено.</p>
          <Button
            title="До списку"
            theme="solid"
            fill="normal"
            size={32}
            onClick={() => history.push('/blog/materials')}
          />
        </ModulePanel>
      </div>
    )
  }

  const saveButton = (
    <Button
      title="Зберегти"
      theme="solid"
      fill="normal"
      icon="save"
      className={styles.saveAction}
      size={32}
      isLoading={isSaving}
      disabled={isPublishing}
      onClick={handleSave}
    />
  )

  const actionButtons = (
    <div className={styles.publicationActions}>
      <div className={styles.publicationMainActions}>
        {saveButton}
        <Button
          title={materialStatus === BLOG_STATUS.published ? 'Оновити на сайті' : 'Опублікувати'}
          theme="solid"
          fill="accent"
          icon="upload"
          size={32}
          isLoading={isPublishing}
          disabled={isSaving}
          onClick={() => handlePublication(true)}
        />
      </div>
      <div className={styles.publicationSecondaryActions}>
        {materialStatus === BLOG_STATUS.published ? (
          <Button
            title="Зняти з публікації"
            theme="transparent"
            color="orange"
            icon="eye-slash"
            size={32}
            disabled={isSaving || isPublishing}
            onClick={() => handlePublication(false)}
          />
        ) : null}
        <Button
          title="Видалити"
          theme="transparent"
          color="red"
          icon="trash-alt"
          size={32}
          disabled={isSaving || isPublishing}
          onClick={() => setShowDelete(true)}
        />
      </div>
    </div>
  )

  return (
    <>
      {showDelete ? (
        <DeleteBlogMaterialPopUp
          material={materialRecord}
          closePopUp={() => setShowDelete(false)}
          onDeleted={(deletedId) => {
            if (activeRouteRef.current !== id || latestRecordRef.current.id !== deletedId) return
            discard()
            history.replace('/blog/materials')
          }}
        />
      ) : null}
      <Prompt when={isSaving || isPublishing || hasUnpersistedChanges} message="Поточні зміни ще не збережено. Залишити редактор?" />
      {autosaveStatus.kind === 'error' ? (
        <div>
          <StatusMessage className="Site-StatusMessage" type="fail" message={autosaveStatus.message} />
          <Button theme="bounded" onClick={downloadBackup}>Завантажити резервну копію</Button>
        </div>
      ) : statusMessage.show && !conflict ? (
        <StatusMessage className="Site-StatusMessage" type={statusMessage.type} message={statusMessage.message} />
      ) : null}
      {conflict ? <BlogVersionConflict conflict={conflict} isSaving={isSaving} onDownload={downloadBackup}
        onResolve={(choice) => { setStatusMessage({ show: false }); void resolveConflict(choice) }} /> : null}
      <div className={styles.editorShell}>
      <ModulePanel
        className={`${styles.panel} ${styles.editorPanel}`}
        title={meta.title || 'Новий матеріал'}
        buttons={actionButtons}
      >
        <div className={styles.metaRow}>
          <span className={styles.muted}>Статус: {getBlogStatusLabel(materialStatus)}</span>
        </div>

        <div className={editorStyles.settingsCard}>
          <button
            type="button"
            className={editorStyles.settingsToggle}
            onClick={() => setSettingsOpen((open) => !open)}
          >
            <span>Налаштування матеріалу</span>
            <span>{settingsOpen ? '▴' : '▾'}</span>
          </button>
          {settingsOpen ? (
            <div className={styles.metaForm}>
              <div className={styles.row3}>
                <Field
                  fieldType="input"
                  config={{ label: 'Назва', type: 'text' }}
                  value={meta.title}
                  changed={handleTitleChange}
                />
                <Field
                  fieldType="input"
                  config={{ label: 'Slug', type: 'text' }}
                  value={meta.slug}
                  changed={handleSlugChange}
                />
                <Field
                  fieldType="select"
                  config={{ label: 'Автор', getOptions: authorOptions }}
                  value={meta.authorId}
                  changed={(event) => patch({ authorId: event.target.value })}
                />
              </div>
              <div className={styles.taxonomyRow}>
                <Field
                  fieldType="select"
                  config={{ label: 'Категорія', getOptions: categoryOptions }}
                  value={meta.categoryId || ''}
                  changed={(event) => patch({
                    categoryId: event.target.value || null,
                    tagIds: meta.tagIds.filter((id) => !tags.some((tag) => tag.id === id && tag.kind === 'category')),
                  })}
                />
                {topicTags.length === 0 ? (
                  <p className={styles.muted}>Тегів ще немає — додайте у вкладці «Категорії та теги».</p>
                ) : (
                  <RelatedBrandsMultiSelect
                    brands={topicTags}
                    value={selectedTags}
                    onChange={handleTagsChange}
                    canBrowseOnFocus
                    label="Теги"
                    emptyMessage="Тегів не знайдено"
                    removeTitle="Прибрати тег"
                    clearTitle="Очистити всі теги"
                  />
                )}
              </div>
              <Field
                fieldType="textarea"
                config={{ label: 'Короткий опис', type: 'text' }}
                value={meta.excerpt}
                changed={(event) => patch({ excerpt: event.target.value })}
              />
              <div className={styles.sectionStack}>
                <p className={styles.sectionTitle}>Обкладинка</p>
                <p className={styles.coverHint}>4:3 · 1600 × 1200 px · JPG або WebP</p>
                <PhotoManager
                  projectId={materialId}
                  projectPath={`blog/${materialId}/cover`}
                  formData={meta.coverPhotos}
                  persistDeletes={false}
                  compact
                  syncState={(photos) =>
                    patch({ coverPhotos: Array.isArray(photos) ? photos.slice(0, 1) : [] })
                  }
                />
              </div>

              <div className={styles.row}>
                <Field
                  fieldType="select"
                  config={{ label: 'SEO review status', getOptions: REVIEW_STATUS_OPTIONS }}
                  value={meta.seoReviewStatus}
                  changed={(event) => patch({ seoReviewStatus: event.target.value })}
                />
                <Field
                  fieldType="select"
                  config={{ label: 'Indexation mode', getOptions: INDEXATION_MODE_OPTIONS }}
                  value={meta.seoIndexationMode}
                  changed={(event) => patch({ seoIndexationMode: event.target.value })}
                />
              </div>

              <div className={styles.row}>
                <div className={styles.seoField}>
                  <Field
                    fieldType="input"
                    config={{ label: 'SEO title', type: 'text' }}
                    value={meta.seoTitle}
                    changed={(event) => patch({ seoTitle: event.target.value })}
                  />
                  <CharCounterHint value={meta.seoTitle} softLimit={SEO_SOFT_LIMITS.title} />
                </div>
                <div className={styles.seoField}>
                  <Field
                    fieldType="input"
                    config={{ label: 'OG title', type: 'text' }}
                    value={meta.seoOgTitle}
                    changed={(event) => patch({ seoOgTitle: event.target.value })}
                  />
                  <CharCounterHint value={meta.seoOgTitle} softLimit={SEO_SOFT_LIMITS.ogTitle} />
                </div>
              </div>
              <div className={styles.row}>
                <div className={styles.seoField}>
                  <Field
                    fieldType="textarea"
                    config={{ label: 'Meta description', type: 'text' }}
                    value={meta.seoDescription}
                    changed={(event) => patch({ seoDescription: event.target.value })}
                  />
                  <CharCounterHint value={meta.seoDescription} softLimit={SEO_SOFT_LIMITS.metaDescription} />
                </div>
                <div className={styles.seoField}>
                  <Field
                    fieldType="textarea"
                    config={{ label: 'OG description', type: 'text' }}
                    value={meta.seoOgDescription}
                    changed={(event) => patch({ seoOgDescription: event.target.value })}
                  />
                  <CharCounterHint value={meta.seoOgDescription} softLimit={SEO_SOFT_LIMITS.ogDescription} />
                </div>
              </div>
              <div className={styles.sectionStack}>
                <p className={styles.sectionTitle}>OG image</p>
                <PhotoManager
                  projectId={`${materialId}-og`}
                  projectPath={`blog/${materialId}/seoOg`}
                  formData={meta.seoOgPhotos}
                  persistDeletes={false}
                  compact
                  syncState={(photos) =>
                    patch({ seoOgPhotos: Array.isArray(photos) ? photos.slice(0, 1) : [] })
                  }
                />
              </div>
            </div>
          ) : null}
        </div>

        <div className={styles.editorSplit}>
          <div className={styles.editorSplitMain}>
            <BlogBlockCanvas
              blocks={blocks}
              materialId={materialId}
              selectedBlockId={selectedBlockId}
              onSelectBlock={setSelectedBlockId}
              onChangeBlocks={setBlocks}
              activeEditor={activeEditor}
              onFocusEditor={setActiveEditor}
            />
          </div>
          <div className={styles.editorSplitPreview}>
            <BlogArticlePreviewPane
              title={meta.title}
              blocks={blocks}
              selectedBlockId={selectedBlockId}
              onSelectBlock={setSelectedBlockId}
            />
          </div>
        </div>
      </ModulePanel>
      <div className={styles.editorActions}>
        {actionButtons}
      </div>
      </div>
    </>
  )
}

export default BlogMaterialEditor
